<script setup lang="ts">
import { provide, watch } from 'vue';
import { createDomRegistry } from './createDomRegistry';
import { sectionNavRegistryKey } from './internal';

const props = defineProps<{
  /** Active item id (v-model). Usually written by whatever scroll-spy the consumer wires. */
  activeId?: string | null;
  /** Forwarded to each item's default `scrollIntoView`. */
  scrollOptions?: ScrollIntoViewOptions;
  /** Replace the per-item scroll action entirely — e.g. a smooth scroll with an active-lock. */
  scrollTo?: (el: HTMLElement) => void;
}>();
const emit = defineEmits<{ 'update:activeId': [id: string | null] }>();

// Registry is type-erased here; items supply the real metadata type T, and
// consumers re-pin it through defineSectionNav<T>() / useSectionNav<T>().
const registry = createDomRegistry<unknown>({
  scrollOptions: props.scrollOptions,
  onScrollIntoView: props.scrollTo,
});
provide(sectionNavRegistryKey, registry);

// Observer-free by design: the active id is written by the consumer's scroll-spy
// or click handling (via this prop or `useSectionNav().setActive`). These two
// watches only bridge that state to/from an optional `v-model:activeId`.
watch(
  () => props.activeId,
  (next) => {
    if (next !== undefined && next !== registry.activeId.value) registry.setActive(next);
  },
  { immediate: true }
);
watch(registry.activeId, (id) => emit('update:activeId', id));
</script>

<template>
  <slot />
</template>
